import React from 'react';

const EmergencyReportPrint = React.forwardRef(({ reportData }, ref) => {
  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (!reportData) return null;

  const { session, summary, rosterEntries, managers } = reportData;

  return (
    <div ref={ref} className="print-container">
      <style jsx>{`
        @media print {
          .print-container {
            font-family: Arial, sans-serif;
            color: #000 !important;
            background: white !important;
            padding: 20px;
            font-size: 12px;
            line-height: 1.4;
          }
          
          .print-header {
            border-bottom: 2px solid #000;
            margin-bottom: 20px;
            padding-bottom: 15px;
          }
          
          .print-title {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 10px 0;
          }
          
          .print-subtitle {
            font-size: 14px;
            color: #666;
            margin: 0;
          }
          
          .print-stats {
            display: flex;
            justify-content: space-between;
            margin-bottom: 25px;
            border: 1px solid #ccc;
            padding: 15px;
          }
          
          .print-stat {
            text-align: center;
            flex: 1;
          }
          
          .print-stat-value {
            font-size: 20px;
            font-weight: bold;
            color: #000;
            margin-bottom: 5px;
          }
          
          .print-stat-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
          }
          
          .print-section {
            margin-bottom: 25px;
          }
          
          .print-section-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            border-bottom: 1px solid #ccc;
            padding-bottom: 5px;
          }
          
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
          }
          
          .print-table th,
          .print-table td {
            border: 1px solid #ccc;
            padding: 8px;
            text-align: left;
            font-size: 11px;
          }
          
          .print-table th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          
          .print-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .print-footer {
            margin-top: 30px;
            border-top: 1px solid #ccc;
            padding-top: 15px;
            font-size: 10px;
            color: #666;
          }
        }
        
        @media screen {
          .print-container {
            background: white;
            padding: 20px;
            font-family: Arial, sans-serif;
            color: #000;
            max-width: 8.5in;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>
      
      <div className="print-header">
        <h1 className="print-title">Emergency Session Report</h1>
        <p className="print-subtitle">{session.name}</p>
        <p className="print-subtitle">
          Generated on {formatDateTime(new Date())}
        </p>
      </div>

      <div className="print-section">
        <h2 className="print-section-title">Session Information</h2>
        <table className="print-table">
          <tbody>
            <tr>
              <td style={{fontWeight: 'bold', width: '30%'}}>Session Type</td>
              <td>{session.type === 'drill' ? 'Drill' : 'Emergency'}</td>
            </tr>
            <tr>
              <td style={{fontWeight: 'bold'}}>Started By</td>
              <td>{session.started_by_name}</td>
            </tr>
            <tr>
              <td style={{fontWeight: 'bold'}}>Start Time</td>
              <td>{formatDateTime(session.started_at)}</td>
            </tr>
            {session.ended_at && (
              <tr>
                <td style={{fontWeight: 'bold'}}>End Time</td>
                <td>{formatDateTime(session.ended_at)}</td>
              </tr>
            )}
            <tr>
              <td style={{fontWeight: 'bold'}}>Duration</td>
              <td>{summary.durationMinutes || 0} minutes</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="print-stats">
        <div className="print-stat">
          <div className="print-stat-value">{summary.totalEmployees}</div>
          <div className="print-stat-label">Total Employees</div>
        </div>
        <div className="print-stat">
          <div className="print-stat-value">{summary.accountedEmployees}</div>
          <div className="print-stat-label">Accounted For</div>
        </div>
        <div className="print-stat">
          <div className="print-stat-value">{summary.completionRate}%</div>
          <div className="print-stat-label">Completion Rate</div>
        </div>
        <div className="print-stat">
          <div className="print-stat-value">{summary.totalEmployees - summary.accountedEmployees}</div>
          <div className="print-stat-label">Unaccounted</div>
        </div>
      </div>

      <div className="print-section">
        <h2 className="print-section-title">Accounted Employees ({rosterEntries.length})</h2>
        <table className="print-table">
          <thead>
            <tr>
              <th style={{width: '30%'}}>Employee Name</th>
              <th style={{width: '25%'}}>Accounted By</th>
              <th style={{width: '20%'}}>Time</th>
              <th style={{width: '25%'}}>Date</th>
            </tr>
          </thead>
          <tbody>
            {rosterEntries.map((entry, index) => (
              <tr key={entry.id || index}>
                <td>{entry.employee_name}</td>
                <td>{entry.accounted_by_name}</td>
                <td>{formatTime(entry.accounted_at)}</td>
                <td>{formatDate(entry.accounted_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="print-section">
        <h2 className="print-section-title">Managers Participated ({managers.length})</h2>
        <table className="print-table">
          <thead>
            <tr>
              <th style={{width: '60%'}}>Manager Name</th>
              <th style={{width: '40%'}}>Employees Accounted</th>
            </tr>
          </thead>
          <tbody>
            {managers.map((manager, index) => (
              <tr key={manager.id || index}>
                <td>{manager.manager_name}</td>
                <td>{manager.employees_accounted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="print-footer">
        <p>
          This report was generated from the KSP Emergency Roster System on {formatDateTime(new Date())}. 
          This document contains sensitive personnel information and should be handled according to company policy.
        </p>
      </div>
    </div>
  );
});

EmergencyReportPrint.displayName = 'EmergencyReportPrint';

export default EmergencyReportPrint;